/**
 * Debounces quantity-sync API calls per line item.
 * Local qty can update on every click; the API fires only after the user pauses.
 */
export class QtySyncDebouncer {
    private pending = new Map<string, {
        timer: ReturnType<typeof setTimeout>;
        baseline: number;
        run: () => void;
    }>();

    constructor(private readonly delayMs = 600) {}

    /**
     * Schedules a sync for `key`. The first `baselineQty` in a burst is kept
     * for rollback if the eventual API call fails.
     */
    schedule(key: string, baselineQty: number, run: (baselineQty: number) => void): void {
        const existing = this.pending.get(key);
        const baseline = existing ? existing.baseline : baselineQty;
        if (existing) {
            clearTimeout(existing.timer);
        }
        const timer = setTimeout(() => {
            this.pending.delete(key);
            run(baseline);
        }, this.delayMs);
        this.pending.set(key, { timer, baseline, run: () => run(baseline) });
    }

    isPending(key: string): boolean {
        return this.pending.has(key);
    }

    hasPending(): boolean {
        return this.pending.size > 0;
    }

    /** Fire all pending syncs immediately (e.g. before proceed-to-buy). */
    flushAll(): void {
        const queued = Array.from(this.pending.values());
        this.pending.forEach(entry => clearTimeout(entry.timer));
        this.pending.clear();
        queued.forEach(entry => entry.run());
    }

    clearAll(): void {
        this.pending.forEach(entry => clearTimeout(entry.timer));
        this.pending.clear();
    }
}
